import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '@/contexts/CartContext';
import { useToast } from '@/contexts/ToastContext';
import { useProducts } from '@/contexts/ProductsContext';
import { IMAGES } from '@/constants/images';

function parsePrice(p: string) {
  return Number(p.replace(/[^0-9]/g, ''));
}

function CartSkeletonItem() {
  return (
    <div className="flex gap-4 items-start animate-pulse">
      <div className="w-16 h-16 rounded-lg bg-[#F0EFED] flex-shrink-0" />
      <div className="flex-1 flex flex-col gap-2 pt-1">
        <div className="h-3.5 bg-[#F0EFED] rounded w-3/4" />
        <div className="h-2.5 bg-[#F0EFED] rounded w-1/3" />
        <div className="flex items-center justify-between mt-2">
          <div className="h-7 bg-[#F0EFED] rounded-full w-20" />
          <div className="h-3.5 bg-[#F0EFED] rounded w-16" />
        </div>
      </div>
    </div>
  );
}

export default function CartDrawer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cart, isCartOpen, closeCart, removeFromCart, updateQty, totalItems, totalPrice } = useCart();
  const { products, routineSet } = useProducts();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);

  const getProductBySlug = (slug: string) => {
    if (slug === '3-step-set') {
      if (!routineSet) return null;
      return {
        id: '3-step-set',
        name: routineSet.name,
        image: IMAGES.threeStepSetLifestyle,
        price: routineSet.price,
        stepKeyword: 'SET',
        volume: '',
      };
    }
    const p = products.find((x) => x.id === slug);
    if (!p) return null;
    return {
      id: p.id,
      name: p.name,
      image: p.image,
      price: p.price,
      stepKeyword: p.stepKeyword,
      volume: p.volume,
    };
  };

  useEffect(() => {
    if (isCartOpen) {
      setLoading(true);
      const timer = setTimeout(() => setLoading(false), 350);
      return () => clearTimeout(timer);
    }
  }, [isCartOpen]);

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-[50] cursor-pointer"
        onClick={closeCart}
      />
      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-[400px] bg-white z-[50] flex flex-col shadow-2xl animate-[slideInRight_0.3s_ease-out]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E8E8E6]">
          <h2 className="text-sm tracking-[0.12em] uppercase font-medium text-[#2A2A2A]">
            {t('cart.yourCart')} ({totalItems} {totalItems === 1 ? t('common.item') : t('common.items')})
          </h2>
          <button
            onClick={closeCart}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F0EFED] transition-colors cursor-pointer"
            aria-label={t('common.close')}
          >
            <i className="ri-close-line text-[#2A2A2A]"></i>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          {loading && cart.length > 0 ? (
            <>
              <CartSkeletonItem />
              <CartSkeletonItem />
            </>
          ) : (
            <>
              {cart.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
                  <div className="w-14 h-14 rounded-full bg-[#F0EFED] flex items-center justify-center">
                    <i className="ri-shopping-bag-line text-2xl text-[#8A8A8A]"></i>
                  </div>
                  <p className="text-sm text-[#8A8A8A] font-light">{t('cart.empty', 'Your cart is empty')}</p>
                </div>
              )}
              {cart.map((item) => {
                const p = getProductBySlug(item.slug);
                if (!p) return null;
                const itemTotal = parsePrice(p.price) * item.qty;
                return (
                  <div key={item.slug} className="flex gap-4 items-start">
                    <div className="w-16 h-16 rounded-lg bg-[#F0EFED] flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <img src={p.image} alt={p.name} className="w-full h-full object-contain p-1" />
                    </div>
                    <div className="flex-1 flex flex-col gap-1.5 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <span className="text-sm font-medium text-[#2A2A2A] leading-tight truncate">{p.name}</span>
                          <span className="text-[10px] tracking-[0.1em] uppercase text-[#7B1113] font-medium">
                            {p.stepKeyword} {p.volume && `· ${p.volume}`}
                          </span>
                        </div>
                        <button
                          onClick={() => {
                            removeFromCart(item.slug);
                            showToast(t('cart.removed', 'Removed from cart'));
                          }}
                          className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-[#F0EFED] transition-colors cursor-pointer flex-shrink-0"
                          aria-label={t('common.remove')}
                        >
                          <i className="ri-close-line text-xs text-[#8A8A8A]"></i>
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <div className="flex items-center gap-2 bg-[#F0EFED] rounded-full px-2 py-1">
                          <button
                            onClick={() => updateQty(item.slug, item.qty - 1)}
                            className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-white transition-colors cursor-pointer"
                            aria-label="Decrease"
                          >
                            <i className="ri-subtract-line text-xs text-[#2A2A2A]"></i>
                          </button>
                          <span className="text-xs font-medium text-[#2A2A2A] w-4 text-center">{item.qty}</span>
                          <button
                            onClick={() => updateQty(item.slug, item.qty + 1)}
                            className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-white transition-colors cursor-pointer"
                            aria-label="Increase"
                          >
                            <i className="ri-add-line text-xs text-[#2A2A2A]"></i>
                          </button>
                        </div>
                        <span className="text-sm font-medium text-[#2A2A2A] whitespace-nowrap">৳{itemTotal.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="border-t border-[#E8E8E6] px-6 py-5 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#8A8A8A] font-light">{t('cart.subtotal', 'Subtotal')}</span>
              <span className="text-sm font-medium text-[#2A2A2A]">৳{totalPrice.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#8A8A8A] font-light">{t('cart.shipping', 'Shipping')}</span>
              <span className="text-xs text-[#8A8A8A] font-light">{t('cart.shippingCalculated', 'Calculated at checkout')}</span>
            </div>
            <button
              onClick={() => {
                closeCart();
                navigate('/checkout');
              }}
              className="w-full py-3.5 bg-[#7B1113] text-white text-xs tracking-[0.15em] uppercase rounded-full hover:bg-[#5C0D0F] transition-colors text-center cursor-pointer whitespace-nowrap"
            >
              {t('common.checkout')} →
            </button>
            <button
              onClick={closeCart}
              className="text-xs text-[#8A8A8A] font-light hover:text-[#2A2A2A] transition-colors text-center cursor-pointer whitespace-nowrap"
            >
              {t('common.continueShopping')}
            </button>
          </div>
        )}
      </div>
    </>
  );
}